import { FC, useMemo } from "react";
import {
  ILinkPreviewCard,
  LinkPreview as LinkPreviewUI,
} from "@business-finland/wif-ui-lib/dist/LinkPreview";
import {
  Block,
  ImageProp,
  ImageStyles,
} from "@business-finland/wif-ui-lib";
import { useRouter } from "next/router";
import { TFunction, useTranslation } from "next-i18next";

import useImageDataHandler, {
  ImageDataHandlerFn,
} from "@/hooks/useImageDataHandler";
import { Viewport } from "@/constants/app";
import { formatPageLink } from "@/util/magnolia";

export type ServicesListProps = {
  services: Wif.Mgnl.ServicePage[];
};

export const ServicesList: FC<ServicesListProps> = (props) => {
  const { services } = props;

  const { isPreview } = useRouter();
  const { t } = useTranslation();
  const imageDataHandler = useImageDataHandler();

  const cards = useMemo(() => {
    return services.map((service) =>
      mapNodeToLinkPreviewCardProps(service, isPreview, t, imageDataHandler)
    );
  }, [services, imageDataHandler, isPreview, t]);

  return (
    <>
      {/*  note: this component is actually a container w/cards */}
      <LinkPreviewUI
        cards={cards}
        hasImages={true}
        hasSaveButton={false}
        backgroundColor="none"
        topPadding={Block.Padding.SMALL}
        newTab={false}
      />
    </>
  );
};

function mapNodeToLinkPreviewCardProps(
  node: Wif.Mgnl.ServicePage,

  isPreview: boolean,
  t: TFunction,
  imageDataHandler: ImageDataHandlerFn
): ILinkPreviewCard {
  const imageData = imageDataHandler(node?.introductionImage, {
    maxViewport: Viewport.xs,
  });
  const imageProps: ImageProp = {
    ...imageData,
    altText: node.title,
    styling: ImageStyles.CARD,
    credits: node.introductionImage?.metadata?.rights,
  };

  // Determine the link and whether to open in new tab
  const serviceType = node.type?.field || "internal";
  const isExternal = serviceType === "external";
  const externalUrl = node.type?.externalUrl;
  const link = isExternal && externalUrl
    ? externalUrl
    : formatPageLink(node["@path"], isPreview);
  const newTab = isExternal;

  return {
    imageProps,
    cardTitle: node.title,
    cardIntroText: node.description,
    link,
    linkLabel: node.ctaText || t("navigation.readMore"),
    linkDescription: "",
    hasImages: true,
    hasSaveButton: false,
    newTab,
    showCTAOnMobile: true,
    badge: node.showWorkInFinlandBadge ? "Work in Finland" : undefined,
  };
}
